import { Campaign, ErrorResponse, ServiceResponse } from '@bzbs/react-api-client';
import { create } from 'zustand';
import { useBuzzebeesAppStore } from './buzzebeess-app.store';
import { useLocaleStore } from './locale.store';

export interface FavoriteCampaignsConfig {
  itemsPerPage?: number;
}

export type FavoriteCampaignsState = {
  data: Campaign[];
  isLoading: boolean;
  hasMore: boolean;
  error: ErrorResponse | null;
  currentPage: number;
  config: FavoriteCampaignsConfig;
  options: {
    [key: string]: unknown;
  };
};

export type FavoriteCampaignsActions = {
  fetchData: (options?: { [key: string]: unknown }) => Promise<ServiceResponse<Campaign[]>>;
  loadMore: () => Promise<ServiceResponse<Campaign[]>>;
  clear: () => void;
  updateConfig: (config: Partial<FavoriteCampaignsConfig>) => void;
};

export type FavoriteCampaignsStore = FavoriteCampaignsState & FavoriteCampaignsActions;

export const createFavoriteCampaignsStore = (initialConfig: FavoriteCampaignsConfig) => {
  return create<FavoriteCampaignsStore>((set, get) => ({
    data: [],
    isLoading: false,
    hasMore: true,
    error: null,
    currentPage: 0,
    config: {
      ...initialConfig,
      itemsPerPage: initialConfig.itemsPerPage || 25,
    },
    options: {},

    updateConfig: (newConfig: Partial<FavoriteCampaignsConfig>) => {
      set((state) => ({
        config: { ...state.config, ...newConfig },
      }));
    },

    fetchData: async (options?: { [key: string]: unknown }) => {
      const { config } = get();
      const localeId = useLocaleStore.getState().localeId;
      const bzbsService = useBuzzebeesAppStore.getState().bzbsService;

      if (!bzbsService) {
        return { type: 'client-error', message: 'Service not configured' };
      }

      set({
        currentPage: 0,
        data: [],
        hasMore: true,
        error: null,
        isLoading: true,
        options: options || {},
      });

      const response = await bzbsService.campaignApi.favoriteCampaigns({
        locale: localeId,
        top: config.itemsPerPage,
        skip: 0,
        ...options,
      });

      if (response.type === 'success') {
        set({
          data: response.model,
          hasMore: response.model.length === config.itemsPerPage,
          isLoading: false,
          error: null,
          currentPage: get().currentPage + 1,
        });
      } else {
        set({ error: response, isLoading: false, hasMore: false });
      }

      return response;
    },

    loadMore: async () => {
      const { config, data, hasMore, currentPage, options } = get();
      const localeId = useLocaleStore.getState().localeId;
      const bzbsService = useBuzzebeesAppStore.getState().bzbsService;

      if (!bzbsService) {
        return { type: 'client-error', message: 'Service not configured' };
      }

      if (!hasMore) {
        return { type: 'client-error', message: 'No more data available' };
      }

      set({ isLoading: true, error: null });
      const response = await bzbsService.campaignApi.favoriteCampaigns({
        locale: localeId,
        top: config.itemsPerPage,
        skip: currentPage * (config.itemsPerPage || 25),
        ...options,
      });

      if (response.type === 'success') {
        set({
          data: [...data, ...response.model],
          hasMore: response.model.length === config.itemsPerPage,
          currentPage: currentPage + 1,
          isLoading: false,
          error: null,
        });
      } else {
        set({
          error: response,
          isLoading: false,
          hasMore: false,
        });
      }

      return response;
    },

    clear: () => {
      set({
        data: [],
        isLoading: false,
        hasMore: true,
        error: null,
        currentPage: 0,
      });
    },
  }));
};
